import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CheckCircle2, Calendar as CalendarIcon, Loader2, Clock, AlertTriangle, FileText, Settings, ShieldAlert, ShieldCheck } from 'lucide-react';
import { toast } from "sonner";
import axios from 'axios';
import { format, isWeekend, isBefore, startOfDay, isSameDay, getDay } from 'date-fns';
import { ro } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceDurations = {
  "Comandă plăcuțe numere": 5,
  "Programare proba teoretică (reducere suspendare)": 5,
  "Programare la proba practică": 5,
  "Înmatriculare / Transcriere auto": 10,
  "Preschimbare permis conducere": 5,
};

const holidays2026 = [
  new Date(2026, 0, 1), new Date(2026, 0, 2),
  new Date(2026, 0, 6), new Date(2026, 0, 7),
  new Date(2026, 0, 24),
  new Date(2026, 3, 10), new Date(2026, 3, 13),
  new Date(2026, 4, 1),
  new Date(2026, 5, 1),
  new Date(2026, 5, 21), new Date(2026, 5, 22),
  new Date(2026, 7, 15),
  new Date(2026, 10, 30),
  new Date(2026, 11, 1),
  new Date(2026, 11, 25), new Date(2026, 11, 26)
];

const isValidCNP = (cnp) => {
  if (typeof cnp !== 'string' || cnp.length !== 13 || isNaN(cnp)) return false;
  const hashResult = [2, 7, 9, 1, 4, 6, 3, 5, 8, 2, 7, 9];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cnp.charAt(i), 10) * hashResult[i];
  }
  let check = sum % 11;
  if (check === 10) check = 1;
  return check === parseInt(cnp.charAt(12), 10);
};

export default function Rezervari() {
  const [searchParams] = useSearchParams();
  const queryService = searchParams.get('service');

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [serviceType, setServiceType] = useState("");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [cnp, setCnp] = useState("");
  const [email, setEmail] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // [NOU] State-uri pentru gestionarea orelor ocupate din Baza de Date
  const [bookedSlots, setBookedSlots] = useState([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);

  useEffect(() => {
    if (queryService && queryService !== serviceType) {
      setServiceType(queryService);
    }
  }, [queryService]);

  useEffect(() => {
    setSelectedTime("");
  }, [selectedDate, serviceType]);

  // [NOU] Efect care interoghează backend-ul pentru a afla orele deja rezervate
  useEffect(() => {
    if (selectedDate && serviceType) {
      const fetchBookedSlots = async () => {
        setIsLoadingSlots(true);
        try {
          const dateStr = format(selectedDate, 'yyyy-MM-dd');
          const response = await axios.get(`${API}/rezervari/slots?date=${dateStr}&service=${encodeURIComponent(serviceType)}`);
          setBookedSlots(response.data || []);
        } catch (error) {
          console.error("Eroare la preluarea orelor indisponibile:", error);
        } finally {
          setIsLoadingSlots(false);
        }
      };
      fetchBookedSlots();
    } else {
      setBookedSlots([]);
    }
  }, [selectedDate, serviceType]);

  const isHoliday = (date) => holidays2026.some(h => isSameDay(date, h));

  const isDateDisabled = (date) => {
    return isWeekend(date) ||
           isBefore(date, startOfDay(new Date())) ||
           isHoliday(date);
  };

  const availableSlots = useMemo(() => {
    if (!selectedDate || !serviceType) return [];

    const duration = serviceDurations[serviceType];
    const ziuaCurenta = getDay(selectedDate);
    const slots = [];

    const genereazaInterval = (startH, startM, endH, endM) => {
      let current = startH * 60 + startM;
      let end = endH * 60 + endM;
      while (current + duration <= end) {
        slots.push(`${String(Math.floor(current/60)).padStart(2, '0')}:${String(current%60).padStart(2, '0')}`);
        current += duration;
      }
    };

    switch (serviceType) {
      case "Programare proba teoretică (reducere suspendare)":
        if ([2, 4].includes(ziuaCurenta)) {
          genereazaInterval(8, 30, 10, 30);
        }
        break;

      case "Programare la proba practică":
        if ([1, 2, 3, 4].includes(ziuaCurenta)) {
          genereazaInterval(14, 0, 16, 0);
        }
        break;

      case "Comandă plăcuțe numere":
      case "Înmatriculare / Transcriere auto":
        if ([1, 2, 3, 4].includes(ziuaCurenta)) {
          genereazaInterval(8, 30, 12, 0);
          genereazaInterval(12, 30, 15, 30);
        } else if (ziuaCurenta === 5) {
          genereazaInterval(8, 30, 12, 0);
          genereazaInterval(12, 30, 13, 30);
        }
        break;

      case "Preschimbare permis conducere":
        if ([1, 2, 4].includes(ziuaCurenta)) {
          genereazaInterval(8, 30, 12, 0);
          genereazaInterval(12, 30, 15, 30);
        } else if (ziuaCurenta === 3) {
          genereazaInterval(8, 30, 12, 0);
          genereazaInterval(12, 30, 18, 0);
        } else if (ziuaCurenta === 5) {
          genereazaInterval(8, 30, 12, 0);
          genereazaInterval(12, 30, 13, 30);
        }
        break;
        
      default:
        break;
    }

    return slots;
  }, [selectedDate, serviceType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValidCNP(cnp)) {
      toast.error("CNP Invalid", { description: "Vă rugăm să introduceți un CNP românesc valid." });
      return;
    }

    if (!selectedDate || !selectedTime) {
      toast.error("Date incomplete", { description: "Vă rugăm să alegeți data și ora." });
      return;
    }

    setIsLoading(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    const formData = {
      serviceType,
      lastName,
      firstName,
      cnp,
      email,
      date: dateStr,
      time: selectedTime
    };

    try {
      await axios.post(`${API}/rezervari`, formData);
      setIsSubmitted(true);
      toast.success("Programare realizată cu succes!", {
        description: `V-ați programat pe ${format(selectedDate, 'dd.MM.yyyy')} la ora ${selectedTime}.`,
      });
    } catch (error) {
      let errorMsg = "Nu am putut procesa programarea. Încercați din nou.";
      if (error.response && error.response.data && error.response.data.detail) {
        errorMsg = error.response.data.detail;
      }
      toast.error("Cerere Respinsă", { description: errorMsg });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="space-y-4 animate-in zoom-in-95 duration-500 flex flex-col items-center justify-center py-10">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-2">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 text-center">Programare Confirmată</h2>
        <p className="text-slate-600 text-center max-w-md text-sm">
          Vă mulțumim, {firstName}! Programarea dvs. a fost înregistrată cu succes în baza noastră de date.
        </p>
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 w-full max-w-md mt-4 shadow-sm">
          <p className="text-xs text-slate-500 mb-2 border-b pb-1">Detalii rezervare:</p>
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex justify-between"><span className="text-slate-500">Serviciu:</span><span className="font-semibold text-right max-w-[200px]">{serviceType}</span></li>
            <li className="flex justify-between"><span className="text-slate-500">Nume complet:</span><span className="font-semibold">{lastName} {firstName}</span></li>
            <li className="flex justify-between"><span className="text-slate-500">CNP:</span><span className="font-semibold">{cnp}</span></li>
            <li className="flex justify-between"><span className="text-slate-500">Data și Ora:</span><span className="font-semibold text-blue-700">{format(selectedDate, 'dd.MM.yyyy')} - {selectedTime}</span></li>
          </ul>
        </div>
        <Button onClick={() => { setIsSubmitted(false); setServiceType(queryService || ""); setLastName(""); setFirstName(""); setCnp(""); setEmail(""); setSelectedDate(null); setSelectedTime(""); }} variant="outline" className="mt-6">
          Efectuează o nouă programare
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <h1 className="text-2xl md:text-3xl font-bold text-slate-800 border-b pb-2">
        Rezervare on line servicii 
        {serviceType && <span className="text-blue-600 font-medium"> / {serviceType}</span>}
      </h1>

      <div className="bg-blue-50 border-l-4 border-blue-600 p-2 rounded-r-lg text-xs text-blue-900 shadow-sm flex flex-col gap-1 items-start">
        <p><strong>Atenție:</strong> Sistemul calculează automat durata alocată pentru fiecare serviciu. Programul exclude sărbătorile legale.</p>
        <p>La ghișeu, vă rugăm să vă prezentați exact la ora selectată, având asupra dumneavoastră actul de identitate și dosarul complet cu documente.</p>
      </div>

      <Card className="shadow-md border-slate-200 mt-2">
        <CardHeader className="bg-slate-50/50 border-b border-slate-100 py-3">
          <CardTitle className="text-lg text-slate-800">Formular de programare</CardTitle>
        </CardHeader>
        <CardContent className="pt-3 pb-4">
          <form onSubmit={handleSubmit} className="space-y-4">

            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="serviceType" className="text-slate-700 text-sm">Tipul serviciului solicitat *</Label>
                <Select onValueChange={setServiceType} required value={serviceType}>
                  <SelectTrigger className="w-full bg-white h-9">
                    <SelectValue placeholder="Selectați serviciul" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Comandă plăcuțe numere">Comandă plăcuțe numere</SelectItem>
                    <SelectItem value="Programare la proba practică">Programare la proba practică</SelectItem>
                    <SelectItem value="Programare proba teoretică (reducere suspendare)">Programare proba teoretică - reducere suspendare</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* PANOU: PROBA PRACTICĂ */}
              {serviceType === "Programare la proba practică" && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden mt-2 animate-in fade-in zoom-in-95 duration-300">
                  <div className="bg-blue-600 text-white font-bold px-3 py-1.5 flex items-center gap-1.5 uppercase tracking-wide text-[11px]">
                    <FileText className="w-3.5 h-3.5" /> Documente și condiții necesare
                  </div>
                  <div className="p-3 text-[12px] text-slate-700 space-y-2 leading-tight">
                    <p className="font-medium text-slate-900 border-b border-slate-200 pb-1.5">
                      Această secțiune este destinată programării sau reprogramării în vederea susținerii probei practice pentru obținerea permisului de conducere sau adăugarea unor categorii noi.
                    </p>
                    <div className="flex gap-2 items-start">
                      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                      <p>La data și ora selectată, este <strong>strict obligatorie prezența la ghișeu</strong> având asupra dumneavoastră <strong>dosarul complet de examinare</strong> și actul de identitate în original.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* PANOU: PLĂCUȚE NUMERE */}
              {serviceType === "Comandă plăcuțe numere" && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden mt-2 animate-in fade-in zoom-in-95 duration-300">
                  <div className="bg-emerald-600 text-white font-bold px-3 py-1.5 flex items-center gap-1.5 uppercase tracking-wide text-[11px]">
                    <ShieldAlert className="w-3.5 h-3.5" /> Emitere plăcuțe de înmatriculare
                  </div>
                  <div className="p-3 text-[12px] text-slate-700 space-y-2 leading-tight">
                    <p className="font-medium text-slate-900 border-b border-slate-200 pb-1.5">
                      Există posibilitatea comandării unor plăcuțe noi cu același număr. Emiterea se face <strong>exclusiv</strong> prin intermediul serviciului nostru.
                    </p>
                    <div>
                      <h4 className="font-bold text-blue-800 flex items-center gap-1 mb-1"><FileText className="w-3.5 h-3.5"/> Acte necesare la ghișeu</h4>
                      <ul className="space-y-0.5 list-disc list-inside marker:text-blue-500 text-[11px]">
                        <li><strong>Act identitate</strong>, <strong>Talon (Certificat)</strong>, și <strong>CIV</strong> (Cartea vehiculului) în original.</li>
                        <li><strong>Dovada plății</strong> (dacă s-a plătit online pe Ghișeul.ro).</li>
                        <li><strong>Plăcuțele vechi</strong> (dacă le schimbați din cauza deteriorării, se predau obligatoriu).</li>
                      </ul>
                      <div className="mt-1.5 bg-blue-50 p-1.5 rounded border border-blue-100 text-[11px] font-medium text-blue-900">
                        <strong>NOUTATE:</strong> Cererea combinată (pentru furt/pierdere/deteriorare) <strong>este emisă direct de sistemul informatic</strong> la ghișeu!
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* PANOU: PROBA TEORETICĂ (REDUCERE SUSPENDARE) */}
              {serviceType === "Programare proba teoretică (reducere suspendare)" && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg overflow-hidden mt-2 animate-in fade-in zoom-in-95 duration-300">
                  <div className="bg-amber-500 text-amber-950 font-bold px-3 py-1.5 flex items-center gap-1.5 uppercase tracking-wide text-[11px]">
                    <Settings className="w-3.5 h-3.5" /> Informații și Documente Obligatorii
                  </div>
                  
                  <div className="p-2.5 text-xs text-slate-700 space-y-2.5 leading-tight">
                    <div className="grid md:grid-cols-2 gap-3">
                      
                      <div className="space-y-2.5">
                        <div>
                          <h4 className="font-bold text-blue-800 flex items-center gap-1 mb-1 border-b border-blue-100 pb-1">
                            <FileText className="w-3.5 h-3.5"/> Pasul 1: Examinarea la SRPCIV
                          </h4>
                          <p className="text-[10.5px] mb-1">Înregistrarea pentru examinare se face prin atribuirea unui cod de bare cu care se poate porni examinarea.</p>
                          <p className="text-[10.5px] font-medium mb-1 mt-1.5 text-slate-800">Pentru susținere, prezentați-vă exclusiv cu:</p>
                          <ul className="list-disc list-inside marker:text-blue-500 space-y-0 text-[11px] font-medium ml-1">
                            <li>Actul de identitate în original;</li>
                            <li>Dovada înlocuitoare a permisului.</li>
                          </ul>
                        </div>

                        <div>
                           <h4 className="font-bold text-emerald-700 flex items-center gap-1 mb-1 border-b border-emerald-100 pb-1">
                             <ShieldCheck className="w-3.5 h-3.5"/> Pasul 2: Transmisia Datelor
                           </h4>
                           <p className="text-[10.5px]">Lucrătorii care au acces la aplicația de Dispecer sau aplicația Abateri au posibilitatea vizualizării rezultatului testului, asigurând un schimb de informații rapid și interconectat între instituții.</p>
                        </div>
                      </div>

                      <div className="bg-white p-2 rounded border border-slate-200 h-fit shadow-sm">
                        <h4 className="font-bold text-red-600 flex items-center gap-1 mb-1.5 text-[11px] border-b pb-1">
                          Nu poate fi înregistrat un conducător auto care:
                        </h4>
                        <ul className="list-none space-y-1 text-[10px] text-slate-600 leading-tight">
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>nu are dreptul de a conduce SUSPENDAT;</li>
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>are deja suspendarea extinsă ca urmare a nepromovării examenului teoretic;</li>
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>a depășit termenul limită pentru examinarea în vederea reducerii suspendării inițiale;</li>
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>are o suspendare pe durată nedefinită (ca urmare a expirării dovezii);</li>
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>a susținut deja un examen în ziua curentă;</li>
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>a susținut deja două examene în săptămâna curentă;</li>
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>a fost deja declarat ADMIS la examenul teoretic pentru suspendarea în curs;</li>
                          <li className="flex gap-1 items-start"><span className="text-red-400 mt-0.5">•</span>are deja un cod de bare atribuit astăzi și nu s-a finalizat examinarea.</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-red-50 p-2.5 rounded border border-red-200">
                      <h4 className="font-bold text-red-900 mb-1 flex items-center gap-1 text-[12px]">
                        <AlertTriangle className="w-4 h-4 shrink-0" /> Pasul 3: ATENȚIE! Promovarea testului NU reduce automat suspendarea!
                      </h4>
                      <div className="text-[11px] text-red-900 leading-snug">
                        <p className="mb-1">Conform O.U.G. nr. 195/2002, reducerea (cu o treime) se dispune <strong>exclusiv la cererea titularului</strong>. Este OBLIGATORIU să parcurgeți pașii:</p>
                        <ol className="list-decimal list-inside space-y-0.5 ml-1">
                          <li><strong>Depunerea cererii scrise:</strong> la Serviciul Rutier pe raza căruia a fost săvârșită fapta.</li>
                          <li><strong>Certificatul de Atestare Fiscală:</strong> eliberat de primăria de domiciliu. Obligatoriu pentru a demonstra achitarea amenzii și lipsa debitelor.</li>
                          <li><strong>Termenul Legal:</strong> toate demersurile se finalizează în primele 20 de zile ale suspendării efective.</li>
                        </ol>
                      </div>
                    </div>

                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-3 pt-1">
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className="text-slate-700 text-xs">Nume *</Label>
                  <Input id="lastName" placeholder="ex: Popescu" className="h-8 text-sm" required value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className="text-slate-700 text-xs">Prenume *</Label>
                  <Input id="firstName" placeholder="ex: Ion" className="h-8 text-sm" required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cnp" className="text-slate-700 text-xs">CNP / CUI *</Label>
                  <Input id="cnp" placeholder="Introduceți CNP-ul" className="h-8 text-sm" required maxLength={13} value={cnp} onChange={(e) => setCnp(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-slate-700 text-xs">Adresă de E-mail *</Label>
                  <Input id="email" type="email" placeholder="nume@exemplu.ro" className="h-8 text-sm" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-3 border-t border-slate-100">
              <h3 className="text-[15px] font-medium text-slate-800 border-b pb-1.5">2. Alegerea datei și a intervalului orar</h3>

              <div className="grid md:grid-cols-2 gap-4 items-start">
                <div className="space-y-2">
                  <Label className="text-slate-700 text-xs">Data dorită *</Label>
                  <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button variant={"outline"} className={`w-full justify-start text-left font-normal h-9 ${!selectedDate && "text-muted-foreground"}`}>
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {selectedDate ? format(selectedDate, "PPP", { locale: ro }) : <span>Alegeți o zi lucrătoare</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={selectedDate} onSelect={(date) => { setSelectedDate(date); setIsCalendarOpen(false); }} disabled={isDateDisabled} initialFocus locale={ro} />
                    </PopoverContent>
                  </Popover>
                  {!serviceType && <p className="text-[11px] text-slate-500 italic">Selectați un serviciu pentru a vedea disponibilitatea.</p>}
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 text-xs">Ora dorită *</Label>
                  <div className="bg-slate-50 border rounded p-2.5 min-h-[140px] max-h-[250px] overflow-y-auto shadow-inner relative">
                    {!selectedDate ? (
                      <div className="h-full min-h-[120px] flex flex-col items-center justify-center text-slate-400">
                        <Clock className="w-6 h-6 mb-1 opacity-50" />
                        <p className="text-[11px] text-center">Selectați data.</p>
                      </div>
                    ) : availableSlots.length === 0 ? (
                      <div className="h-full min-h-[120px] flex flex-col items-center justify-center text-red-400">
                        <p className="text-[11px] text-center px-4">Nu există locuri configurate pentru această zi.</p>
                      </div>
                    ) : isLoadingSlots ? (
                      <div className="h-full min-h-[120px] flex flex-col items-center justify-center text-blue-500">
                        <Loader2 className="w-6 h-6 mb-2 animate-spin" />
                        <p className="text-[11px] text-center">Se verifică disponibilitatea...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-4 gap-1.5">
                        {availableSlots.map(time => {
                          const isBooked = bookedSlots.includes(time); // VERIFICARE TIMP OCUPAT
                          return (
                            <button
                              key={time}
                              type="button"
                              disabled={isBooked}
                              onClick={() => setSelectedTime(time)}
                              className={`py-1.5 px-1 text-[11px] font-medium rounded border transition-colors ${
                                isBooked 
                                  ? 'bg-slate-200 text-slate-400 border-slate-300 cursor-not-allowed opacity-60 line-through'
                                  : selectedTime === time 
                                    ? 'bg-blue-600 text-white border-blue-600 ring-1 ring-blue-300' 
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:bg-blue-50'
                              }`}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {selectedTime && <p className="text-xs text-blue-700 font-medium bg-blue-50 p-1.5 rounded border border-blue-100 text-center mt-2">Interval rezervat: <span className="text-sm font-bold">{selectedTime}</span></p>}
                </div>
              </div>
            </div>

            <div className="pt-2 flex justify-end">
              <Button type="submit" disabled={isLoading || !selectedDate || !selectedTime} className="bg-blue-600 hover:bg-blue-700 text-white min-w-[180px] h-10 text-sm shadow-md">
                {isLoading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Procesare...</> : "Finalizează Programarea"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}